import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined';
import { ApiError } from '../api/client';
import { organizationUsersApi, type OrganizationUser, type OrganizationUserRole } from '../api/endpoints';
import { PageHeader } from '../components/PageHeader';
import { useToast } from '../app/ToastProvider';

const roles: OrganizationUserRole[] = ['MANAGER', 'BILLING', 'VIEWER'];

export function UsersPage() {
  const [open, setOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState<OrganizationUser | null>(null);
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [form, setForm] = useState({ username: '', password: '', firstName: '', lastName: '', email: '', role: 'VIEWER' as OrganizationUserRole });
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data = [], isError } = useQuery({ queryKey: ['organization-users'], queryFn: organizationUsersApi.list });

  const create = useMutation({
    mutationFn: () => organizationUsersApi.create(form),
    onSuccess: async ({ message }) => {
      toast.success(message);
      setOpen(false);
      setForm({ username: '', password: '', firstName: '', lastName: '', email: '', role: 'VIEWER' });
      await queryClient.invalidateQueries({ queryKey: ['organization-users'] });
    },
    onError: (caught: unknown) => {
      toast.error(caught instanceof ApiError ? caught.message : 'The organization user could not be created.');
    },
  });
  const update = useMutation({
    mutationFn: ({ id, values }: { id: number; values: { role?: OrganizationUserRole; isActive?: boolean } }) =>
      organizationUsersApi.update(id, values),
    onSuccess: async ({ message }) => {
      toast.success(message);
      await queryClient.invalidateQueries({ queryKey: ['organization-users'] });
    },
  });
  const resetPassword = useMutation({
    mutationFn: () => organizationUsersApi.resetPassword(resetTarget?.id as number, temporaryPassword),
    onSuccess: ({ message }) => {
      toast.success(message);
      setResetTarget(null);
      setTemporaryPassword('');
    },
    onError: (caught: unknown) => {
      toast.error(caught instanceof ApiError ? caught.message : 'The password could not be reset.');
    },
  });
  const set = (key: keyof typeof form, value: string) => setForm({ ...form, [key]: value });
  const fieldError = (name: keyof typeof form) =>
    create.error instanceof ApiError ? create.error.byField()[name] : undefined;
  const closeDialog = () => {
    create.reset();
    setOpen(false);
  };
  const openDialog = () => {
    create.reset();
    setOpen(true);
  };

  return <>
    <PageHeader title="Users" description="Manage users and access roles within your organization." actions={<Button variant="contained" startIcon={<AddIcon />} onClick={openDialog}>Add user</Button>} />
    {isError ? <Alert severity="error" sx={{ mb: 2 }}>Organization users could not be loaded.</Alert> : null}
    <Card><Table><TableHead><TableRow><TableCell>User</TableCell><TableCell>Email</TableCell><TableCell>Role</TableCell><TableCell>Status</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead><TableBody>{data.map((user: OrganizationUser) => <TableRow key={user.id}><TableCell>{user.firstName} {user.lastName}<br /><small>{user.username}</small></TableCell><TableCell>{user.email}</TableCell><TableCell><TextField select size="small" value={user.role} onChange={(event) => update.mutate({ id: user.id, values: { role: event.target.value as OrganizationUserRole } })}>{roles.map((role) => <MenuItem key={role} value={role}>{role.replace('_', ' ')}</MenuItem>)}</TextField></TableCell><TableCell><Switch checked={user.isActive} onChange={(event) => update.mutate({ id: user.id, values: { isActive: event.target.checked } })} /></TableCell><TableCell align="right"><Button size="small" startIcon={<LockResetOutlinedIcon />} onClick={() => { resetPassword.reset(); setTemporaryPassword(''); setResetTarget(user); }}>Reset Password</Button></TableCell></TableRow>)}</TableBody></Table></Card>
    <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="sm">
      <DialogTitle>Add organization user</DialogTitle>
      <form onSubmit={(event) => { event.preventDefault(); create.mutate(); }}>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {create.isError ? (
              <Alert severity="error">
                {create.error instanceof ApiError ? create.error.message : 'The organization user could not be created.'}
              </Alert>
            ) : null}
            <TextField label="Username" value={form.username} onChange={(e) => set('username', e.target.value)} required error={Boolean(fieldError('username'))} helperText={fieldError('username') ?? 'Use at least 3 characters.'} slotProps={{ htmlInput: { minLength: 3, maxLength: 50 } }} />
            <TextField label="Temporary password" type="password" value={form.password} onChange={(e) => set('password', e.target.value)} required error={Boolean(fieldError('password'))} helperText={fieldError('password') ?? 'Use at least 8 characters.'} slotProps={{ htmlInput: { minLength: 8, maxLength: 128 } }} />
            <TextField label="First name" value={form.firstName} onChange={(e) => set('firstName', e.target.value)} required error={Boolean(fieldError('firstName'))} helperText={fieldError('firstName')} slotProps={{ htmlInput: { maxLength: 100 } }} />
            <TextField label="Last name" value={form.lastName} onChange={(e) => set('lastName', e.target.value)} required error={Boolean(fieldError('lastName'))} helperText={fieldError('lastName')} slotProps={{ htmlInput: { maxLength: 100 } }} />
            <TextField label="Email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} required error={Boolean(fieldError('email'))} helperText={fieldError('email')} />
            <TextField select label="Role" value={form.role} onChange={(e) => set('role', e.target.value)} error={Boolean(fieldError('role'))} helperText={fieldError('role')}>{roles.map((role) => <MenuItem key={role} value={role}>{role.replace('_', ' ')}</MenuItem>)}</TextField>
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={closeDialog}>Cancel</Button><Button type="submit" variant="contained" disabled={create.isPending}>Create user</Button></DialogActions>
      </form>
    </Dialog>
    <Dialog open={Boolean(resetTarget)} onClose={resetPassword.isPending ? undefined : () => setResetTarget(null)} fullWidth maxWidth="xs">
      <DialogTitle>Reset user password</DialogTitle>
      <form onSubmit={(event) => { event.preventDefault(); resetPassword.mutate(); }}>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning">
              Set a temporary password for {resetTarget?.username}. They will be required to replace it the next time they sign in.
            </Alert>
            {resetPassword.isError ? <Alert severity="error">{resetPassword.error instanceof ApiError ? resetPassword.error.message : 'The password could not be reset.'}</Alert> : null}
            <TextField label="Temporary password" type="password" value={temporaryPassword} onChange={(event) => setTemporaryPassword(event.target.value)} helperText="Use at least 8 characters." slotProps={{ htmlInput: { minLength: 8, maxLength: 128 } }} autoComplete="new-password" required autoFocus />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetTarget(null)} disabled={resetPassword.isPending}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={resetPassword.isPending}>{resetPassword.isPending ? 'Resetting...' : 'Reset Password'}</Button>
        </DialogActions>
      </form>
    </Dialog>
  </>;
}
